import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';

import { DocumentService } from '../../services/document.service';
import {
  SiteVisitService,
  SiteVisitTransitionOutcome,
  SiteVisitTransitionPayload,
  SiteVisitView,
} from '../../services/site-visit.service';
import {
  SiteVisitActorContext,
  SiteVisitStatus,
  SiteVisitTransition,
  canAssociate,
  isTerminalStatus,
  statusLabel,
  statusPillClass,
} from '../site-visit-state.util';
import {
  ArrivalTimeOption,
  arrivalTimeOptions,
  DEFAULT_ARRIVAL_TIME,
  isTimeInWindow,
  renderVisitDay,
  renderVisitTime,
  timeWindowRange,
  toDateInputValue,
} from '../site-visit-time.util';

/**
 * The professional associate's site-visit view: **one state-driven page, not a wizard**, mirroring the
 * prototype's `SchedulingClient`. Every one of the twelve states gets a human label so the PA always
 * knows where the negotiation stands, and whichever state is actionable also gets its affordance —
 * the proposal form, accept-or-counter, the reschedule disclosure, the calendar link, completion.
 *
 * **One appointment, proposed and agreed in one step.** A proposal is a calendar day *and* an exact
 * arrival time, sent together by {@link submitAvailability}; from there exactly one proposal is
 * outstanding and exactly one party owes a reply. When it is the PA's, this page offers the two
 * answers the table allows — `PA_ACCEPT` and `PA_COUNTER` — and the counter form is the same two
 * fields the proposal used. There is no second round that agrees a clock time, no alternatives list to
 * pick from, and no AM/PM window anywhere: the arrival time is part of the offer from the first move.
 *
 * **Legality is derived, not hand-written.** Every affordance in the template is gated on
 * {@link can}, which delegates to `canAssociate` in `site-visit-state.util.ts` — a port of
 * connect-service's `SiteVisitTransitionEnum`. So a transition illegal for the current state is
 * *absent* rather than offered and rejected, and {@link dispatch} re-checks the same predicate before
 * the call goes out, so no code path can post a move the table forbids. The server validates again;
 * this is the second lock.
 *
 * **The completion form (access issues, photos, concerns, notes) IS ported now** — Mocks A1–A3.
 * `completionOutcome` selects the transition the way the prototype's `completed` boolean selected a
 * status: `COMPLETE` when the visit came off, `RESCHEDULE` — carrying the very same report — when it
 * did not, because `RESCHEDULE` already lands on `PENDING_SITE_CONTACT_APPROVAL` and does everything a
 * dedicated "failed visit" transition would have. See {@link submitCompletion}.
 *
 * 🔴 **Every move that puts an appointment on the table sends a day AND an arrival time** —
 * `PROPOSE_VISIT`, `PA_COUNTER` and both `RESCHEDULE` paths. The handler requires the pair, so a form
 * that collected only a day would be refused server-side on every submission.
 *
 * 🕐 Times and days are rendered from the server's strings and never converted here — see
 * `site-visit-time.util.ts` for why that is a guardrail rather than a preference.
 */
@Component({
  selector: 'site-visit-negotiation',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './site-visit-negotiation.component.html',
  styleUrl: './site-visit-negotiation.component.scss',
})
export class SiteVisitNegotiationComponent implements OnChanges, OnInit, OnDestroy {
  /** The negotiation. `null` means this site has none, and the view renders nothing. */
  @Input() visit: SiteVisitView | null = null;

  /**
   * Read-only mode (the project-history record). Every label still renders; no affordance does —
   * `can()` returns false throughout, which is the same mechanism that hides an illegal transition.
   */
  @Input() readOnly: boolean = false;

  /**
   * The surface hosting this panel already names the visit, so the panel must not name it again.
   *
   * 🔴 Set by the two queue surfaces - the cross-project scheduling page and the workspace card -
   * where the panel is projected INTO a row that has already rendered the status pill, the order
   * number, the property and the address directly above it. Rendering the header there put the same
   * four facts on screen twice, a few pixels apart. The standalone per-project view has no row above
   * it, so it defaults to `false` and keeps its header.
   *
   * <p>The attempt count survives either way: it is on the detail read and NOT on the queue row, so
   * it is the one thing a host cannot have shown, and suppressing the header outright would lose it.
   */
  @Input() identityInHost = false;

  /**
   * This panel was opened by a control named *Reschedule*, so the reschedule section starts unfolded.
   *
   * 🔴 **This does not reverse the collapse decision — the two rules coexist, and the distinction IS
   * the feature.** Collapsed-by-default (see {@link rescheduleOpen}) governs opening the panel
   * GENERICALLY: *View Details*, the row toggle, a `?visitId=` deep link, the per-project view. None of
   * those says anything about which form the PA wants, so the panel keeps one shape and the five-field
   * exception path stays out of the way of the form they probably came for. A click on a button
   * LABELLED *Reschedule* is the opposite: an explicit declaration of intent, by name. Honouring it is
   * not "sometimes expanded for no visible reason" — it is the panel doing what the button said. Do not
   * collapse these two into one rule in either direction: making generic opens expand re-creates the
   * problem the disclosure was added to solve, and making this one collapse restores the second click
   * this input exists to remove.
   *
   * 🔴 **Read once, in `ngOnInit`, and deliberately not from `ngOnChanges`.** `ngOnChanges` runs on any
   * input change — the host re-binds `visit` after every accepted move and after the detail read folds
   * back — so re-evaluating there would re-open the section under a PA who had just deliberately
   * collapsed it mid-edit, and no amount of typing would keep it shut. Once the panel is up, the
   * disclosure belongs to {@link toggleReschedule} alone.
   *
   * The intent must not stick, and that is the HOST's half of the contract rather than this one's: both
   * queue surfaces clear the flag they pass here whenever a row is opened generically, closed, or a move
   * is accepted, so a panel reopened through *View Details* is bound `false` and starts collapsed. This
   * component holds no memory across mounts to leak — the hosts render it under an `@if` on the open
   * row, so each open builds a fresh instance and `ngOnInit` runs again.
   */
  @Input() startRescheduleExpanded = false;

  /** The negotiation as it now stands, after a move landed. */
  @Output() visitChange = new EventEmitter<SiteVisitView>();

  // --- the proposal form (NEEDS_AVAILABILITY) ---------------------------------------------------
  // One step: the day and the arrival time go out together on `PROPOSE_VISIT`. `availabilityWindow` is
  // gone with the AM/PM model, and `availabilityTime` is not optional — the handler refuses a proposal
  // that names no time.
  availabilityDay: string = '';
  availabilityTime: string = DEFAULT_ARRIVAL_TIME;
  availabilityDuration: string = '2 hours';
  availabilityArrivalPlan: string = '';

  // --- counter the outstanding proposal (PENDING_PA_APPROVAL) -----------------------------------
  // The mirror of the form above, minus the two PA-owned fields: `PA_COUNTER`'s write whitelist does
  // not include `estimatedDuration` or `arrivalPlan`, so offering them here would collect edits the
  // server drops on the floor. Both fields are seeded from the proposal on the table when the form is
  // opened, so changing only the day — or only the time — is one edit rather than two.
  counterDay: string = '';
  counterTime: string = '';

  // --- move an agreed appointment (RESCHEDULE) ---------------------------------------------------
  // Its own fields rather than a share of the counter's, even though the two forms can never render
  // together (`PENDING_PA_APPROVAL` versus the day-agreed states). What is reused is the pattern and
  // the payload — the same `type="date"` control, the same arrival-time picker, the same
  // `day`/`arrivalTime`/`arrivalPlan` keys through the same `dispatch` — not the storage, which would
  // read as a bug to anyone who found `counterDay` holding a reschedule.
  rescheduleDay: string = '';
  rescheduleTime: string = '';
  rescheduleReason: string = '';
  rescheduleArrivalPlan: string = '';

  // --- post-visit completion (Mocks A1-A3; COMPLETE / RESCHEDULE carrying a report) --------------
  // One report, and which segment is picked decides the transition — `COMPLETE` when the visit came
  // off, `RESCHEDULE` (carrying this SAME report) when it did not. Gated on `can('COMPLETE')` alone in
  // the template: `COMPLETE` (`ASSOCIATE_OR_SYSTEM`) and `RESCHEDULE` (`ASSOCIATE_ONLY`) share the
  // identical legal-from set and both admit `PROFESSIONAL_ASSOCIATE` unconditionally, so for this actor
  // the two are always legal together — see `permittedActors` in `site-visit-state.util.ts`.
  completionOutcome: 'COMPLETED' | 'FAILED' = 'COMPLETED';
  completionAccessIssues = false;
  completionConcerns = false;
  completionNotes = '';
  completionAccessIssueFiles: File[] = [];
  completionConcernFiles: File[] = [];

  // Collected only on the FAILED branch — day, time and reason ride the SAME `RESCHEDULE` the plain
  // "move the agreed appointment" disclosure uses, so they get their own fields rather than a share of
  // `rescheduleDay`/`rescheduleTime`/`rescheduleReason`: the two forms can render on screen at the
  // same time (both are legal from `TIME_CONFIRMED`) and must not fight over one value.
  completionDay: string = '';
  completionTime: string = '';
  completionReason: string = '';

  /**
   * The ids a report group's photos uploaded to, or `null` when nothing has been uploaded for the
   * CURRENT file selection. Caches the upload so a refused transition can be retried without
   * re-posting the same files — see {@link submitCompletion}. Invalidated (`null`) the instant the
   * selection changes or the group is unchecked; a resubmit with the same files reuses the ids, a
   * resubmit after picking new ones uploads them.
   */
  private completionAccessIssuePhotoIds: string[] | null = null;
  private completionConcernPhotoIds: string[] | null = null;

  /** `URL.createObjectURL` results for the files above, so a preview is not re-minted on every render. */
  private completionPreviewUrls = new Map<File, string>();

  /**
   * The server's refusal on the LAST completion submit, or `null`. Populated only from an
   * `INVALID_PAYLOAD` outcome (see {@link submitCompletion}) and cleared on every edit, so a stale
   * refusal never survives past the field the PA is now fixing. Rendered inline on the panel (Mock
   * A2) rather than as a toast — the design note is explicit that this rule is a server error to
   * show, not only a disabled button, because the rule lives in the handler and any other caller hits
   * the same wall.
   */
  completionRefusal: string | null = null;

  /**
   * Whether the reschedule form is showing. **Presentational only** — it decides nothing about
   * legality, which stays with `can('RESCHEDULE')`, and nothing about submission, which stays with
   * {@link canSubmitReschedule}. The disclosure sits INSIDE the `can()` gate, so a state the table
   * forbids the move from offers neither the form nor the thing that opens it.
   *
   * 🔴 **Collapsed at every state it is legal from, including the ones with no competing form.**
   * RESCHEDULE is legal from `TIME_CONFIRMED` and from the three retired day-agreed states an
   * orphaned row can still sit in; only `TIME_CONFIRMED` also renders the completion form, and there
   * the five-field exception path visually outweighed the report the PA actually came to file.
   * Auto-expanding at the other three — where the reschedule is the sole action, so collapsing it
   * costs a click for nothing — was considered and rejected: a panel that changes shape between states
   * for a reason the PA cannot see is worse than one that behaves the same everywhere. One shape,
   * always.
   *
   * Collapsed is also the state on every GENERIC mount. Both queue surfaces render the panel under an
   * `@if` on the opened row, so closing a row destroys this component and reopening it builds a fresh
   * one — the field initialiser IS the "starts collapsed again" rule, and no re-bind hook is needed to
   * enforce it. Deliberately NOT reset from `ngOnChanges`: that runs on any input change, so a host
   * that re-bound the visit while the PA was mid-sentence would close the form under them.
   *
   * 🔴 The one route in that starts it OPEN is {@link startRescheduleExpanded}, applied once in
   * `ngOnInit` — the row's *Reschedule* button, which named the form it wanted. That is an exception to
   * the shape rule above only in the sense that it answers a different QUESTION: "what does the panel
   * look like when nobody asked?" versus "what does it look like when somebody asked for this by
   * name?". The paragraph above still governs every other way in, unchanged. See that input for why
   * neither rule may be folded into the other.
   */
  rescheduleOpen: boolean = false;

  /**
   * Whether the counter form is showing at `PENDING_PA_APPROVAL`. **Presentational only**, exactly as
   * {@link rescheduleOpen} is: legality stays with `can('PA_COUNTER')` and submission with
   * {@link canSubmitCounter}, and the form sits INSIDE that gate.
   *
   * Closed by default so the page leads with the one-tap answer. Accepting the appointment on the
   * table is both the commonest reply and the one that ends the negotiation, so it is a single button;
   * countering is a second click into two fields, which is the right cost for the move that puts
   * another round on the table. Collapsing does not clear what has been typed — see
   * {@link toggleCounter}, which follows the reschedule disclosure's rule for the same reason.
   */
  counterOpen: boolean = false;

  /** {@link DEFAULT_ARRIVAL_TIME}, reachable from the template and from the spec's assertions. */
  readonly defaultArrivalTime = DEFAULT_ARRIVAL_TIME;

  // The reason cap, mirroring connect-service's `ValidationUtils.MAX_NOTE_LENGTH` — the same number the
  // comments composer mirrors for a review-thread message, and for the same reason: the server refuses
  // anything longer, so a form that let the PA past it would be promising something the API will not
  // honour. Bound as `maxlength` + a live counter (the comments-card idiom), and re-asked in
  // `canSubmitReschedule` against the TRIMMED value, which is exactly what the server measures.
  readonly maxReasonLength = 255;

  /** Two-click guard on the terminal cancel, so a misclick cannot end a negotiation. */
  confirmingCancel: boolean = false;

  /**
   * Why the negotiation is being ended, and **required** — the same footing `rescheduleReason` is on,
   * against the same {@link maxReasonLength} and the same trimmed measurement, because the server
   * validates the two with one method.
   *
   * 🔴 **The asymmetry this closes ran backwards.** `RESCHEDULE` is reversible and has always demanded
   * a reason; `CANCEL` is the one that cannot be undone — its own copy says so — and it sent
   * `dispatch('CANCEL', {})`, capturing nothing. So this is the field that was missing, not a new rule.
   *
   * Its own field rather than a share of `rescheduleReason`: the reschedule form and the cancel
   * confirmation are both reachable from `TIME_CONFIRMED`, so they can be on screen together and must
   * not fight over one value — the same reason `completionReason` is separate from both.
   *
   * The **placement** of the affordance is deliberately unchanged: still behind its disclosure, still
   * a two-click confirm. Low discoverability is right for a move that cannot be undone; what was
   * missing was the record of why.
   */
  cancelReason: string = '';

  isSubmitting: boolean = false;
  calendarUrl: string | null = null;

  constructor(
    private siteVisitService: SiteVisitService,
    private documentService: DocumentService,
    private toastr: ToastrService
  ) {}

  /** Object URLs are revoked on destroy too, not only on a reset — see {@link resetCompletionForm}. */
  ngOnDestroy(): void {
    this.completionPreviewUrls.forEach((url) => URL.revokeObjectURL(url));
  }

  /**
   * Adopt the opener's intent, ONCE.
   *
   * 🔴 `ngOnInit` and not `ngOnChanges`, and that is the whole guarantee: this runs one time per mount,
   * so the disclosure answers to {@link toggleReschedule} from here on. `ngOnChanges` fires on every
   * re-bind — the host writes `detail` again after the fold-back read and after every accepted move —
   * and re-reading the input there would snap the section back open under a PA who had just collapsed
   * it, over and over, with nothing on screen explaining why.
   *
   * Assigned only when the input is true, so this can never CLOSE a section: `rescheduleOpen` is false
   * at this point in the lifecycle either way, and writing `false` here would read as a reset hook that
   * a later refactor could feel licensed to move into `ngOnChanges`.
   */
  ngOnInit(): void {
    if (this.startRescheduleExpanded) {
      this.rescheduleOpen = true;
      // 🔴 Seeded here as well as in {@link toggleReschedule}, because this is the way IN that the row's
      // *Reschedule* button uses — the primary one. Opening the section without it would hand the
      // commonest path an empty arrival time and a disabled submit while the disclosure path arrived
      // pre-filled, which is the kind of difference nobody sees until a PA reports the button "does
      // nothing".
      this.seedRescheduleTime();
    }
  }

  ngOnChanges(): void {
    // Seed the proposal's time whenever the negotiation arrives or advances, so the PA opens a form
    // that is already submittable rather than one whose submit is disabled for a field they have not
    // been asked about yet. An exact time is required now, so an unseeded picker would read as a
    // broken form.
    //
    // The condition asks whether the held value is a usable time rather than whether it is empty, so
    // anything non-canonical is replaced — but a time the PA has actually chosen survives the re-bind
    // the host performs after every accepted move, which is the behavior that matters the other way.
    if (!isTimeInWindow(this.availabilityTime)) {
      this.availabilityTime = this.defaultArrivalTime;
    }
    this.confirmingCancel = false;
  }

  // --- state ------------------------------------------------------------------------------------

  get status(): SiteVisitStatus | null {
    return this.visit?.status ?? null;
  }

  /** The resolving actor rules' one remaining input — `routedToPm`, which replaces the party. */
  get actorContext(): SiteVisitActorContext {
    return {
      routedToPm: !!this.visit?.routedToPm,
    };
  }

  get label(): string {
    return statusLabel(this.status, this.actorContext);
  }

  get pillClass(): string {
    return statusPillClass(this.status);
  }

  get isTerminal(): boolean {
    return isTerminalStatus(this.status);
  }

  /**
   * The one gate. `readOnly` and a missing negotiation both close every affordance; otherwise the
   * derived transition table decides. The template asks this and nothing else.
   */
  can(transition: SiteVisitTransition): boolean {
    if (this.readOnly || !this.visit) {
      return false;
    }
    return canAssociate(transition, this.status, this.actorContext);
  }

  /**
   * The site contact's link can be re-sent while the negotiation is parked awaiting them. This is not
   * a transition — it changes no state — so it is gated on the state directly rather than through the
   * legality table. It is the only recovery path for a link that was never delivered at all (bad
   * address, spam filter), which no public dead-link page can help with.
   */
  get canResendLink(): boolean {
    return (
      !this.readOnly &&
      !!this.visit &&
      this.status === 'PENDING_SITE_CONTACT_APPROVAL'
    );
  }

  /** Whose approval is outstanding — routing replaces the party, so the copy must follow it. */
  get awaitingParty(): string {
    return this.visit?.routedToPm ? 'project manager' : 'site contact';
  }

  /**
   * 🔴 Party details are revealed **only once the appointment is agreed** — a security property
   * carried from the prototype, not a UX detail. This getter is the single condition the template
   * renders the parties block under, so a projection that wrongly carried them earlier still could not
   * display them.
   *
   * 🔴 **`TIME_CONFIRMED` and nothing else, because that is the only state the wire populates.**
   * `AssociateSiteVisitView.partiesFor` returns null for every other status, and
   * `AssociateSiteVisitQueueRow` calls that same method rather than copying its test — so there is one
   * reveal gate on the server and this is its mirror. `SITE_VISIT_COMPLETED` used to be here too and
   * could never be true against real data: the projection stops emitting `parties` the moment a
   * completion is submitted. Reading it as a second agreed state made this getter *look* wider than
   * the server while behaving no differently, and a spec fixture that hand-set `parties` on a
   * completed visit kept the dead arm permanently green.
   *
   * Widening it is therefore a **two-sided** change: this side alone cannot show what the server does
   * not send, and the server's own javadoc argues against sending it after completion. The four
   * pre-agreement states (`NEEDS_AVAILABILITY`, `AWAITING_SITE_CONTACT_INFO`,
   * `PENDING_SITE_CONTACT_APPROVAL`, `PENDING_PA_APPROVAL`) disclose nothing, and neither do the five
   * retired ones: an orphaned row never reached an agreed arrival time, so it is a pre-agreement row
   * by every meaning the boundary has.
   */
  get partiesVisible(): boolean {
    return !!this.visit?.parties && this.status === 'TIME_CONFIRMED';
  }

  // --- rendering (never converting) -------------------------------------------------------------

  /**
   * 🔴 The rendered-day argument is `null` deliberately, not left open for a field to be threaded
   * back into. `AssociateSiteVisitView` carries no `display*Day` and argues at length that it never
   * should: a UTC-noon-pinned calendar day is read back in UTC without resolving a zone offset, which
   * is exactly what `renderVisitDay`'s fallback does — and it keeps this side's `format` argument,
   * which a server-rendered string would silently override.
   */
  get proposedDayText(): string {
    return renderVisitDay(null, this.visit?.proposedDate);
  }

  /**
   * The agreed day, and **only** the agreed day.
   *
   * 🔴 The fallback to {@link proposedDayText} that used to close this expression is gone. Every
   * template site labels this value "Agreed:" or "Confirmed:", and the proposed day is by definition
   * not agreed — after a counter it is precisely the day that was rejected. It was also the exact
   * shape of the `approvedDate`/`confirmedDate` naming bug `SiteVisitView.confirmedDate` documents:
   * a key that read `undefined` forever while this getter silently answered with the proposed day.
   *
   * It was unreachable at `TIME_CONFIRMED` and fired only on orphaned rows in the retired day-agreed
   * states — which carry `confirmedDate` anyway (`SiteVisitSchedule`: a `confirmedDate` with no
   * `confirmedTime` is exactly what an orphan looks like), so nothing lost a day it used to show.
   */
  get agreedDayText(): string {
    return renderVisitDay(null, this.visit?.confirmedDate);
  }

  get proposedTimeText(): string {
    return renderVisitTime(this.visit?.displayProposedTime, this.visit?.proposedTime);
  }

  get confirmedTimeText(): string {
    return renderVisitTime(this.visit?.displayConfirmedTime, this.visit?.confirmedTime);
  }

  /**
   * The arrival times on offer, across the whole day (Task 10 dropped the window bound). Built as an
   * explicit list rather than left to the browser: the native time picker ignores `min`/`max` when it
   * draws its list. Passing the held value keeps an off-grid time from a previous proposal selectable,
   * which is why this takes the field it is being drawn for rather than reading one fixed field —
   * four pickers on this page each hold their own value.
   */
  arrivalTimeChoices(selected: string | null | undefined): ArrivalTimeOption[] {
    return arrivalTimeOptions(selected);
  }

  /** The soft-warning threshold, spelled out beside the picker — see `ARRIVAL_TIME_WARNING_RANGE`. */
  get timeRangeText(): string {
    const range = timeWindowRange();
    return `${renderVisitTime(null, range.min)}–${renderVisitTime(null, range.max)}`;
  }

  /** Echo of a time the PA has chosen. Lexical, so it carries no zone and no parenthetical. */
  timeText(value: string | null | undefined): string {
    return renderVisitTime(null, value);
  }

  /**
   * 🔴 **A soft warning, never a refusal.** A time outside {@link ARRIVAL_TIME_WARNING_RANGE}'s
   * 5:00 AM – 7:00 PM is a perfectly legal selection — the picker offers the whole day and the server
   * accepts it — so this decides only whether the form says so beside the field. A 6:00 AM arrival to
   * beat a tenant's opening hours is a real request; the design's decision 7 is explicit that the
   * unusual-hours signal is carried by a warning rather than by narrowing the options.
   *
   * A non-canonical value warns about nothing: it is an unfilled field, and the submit gate is what
   * speaks to that. The comparison is lexical, which is exact for zero-padded `HH:mm` — the string
   * order and the clock order are the same order.
   */
  isOutsideWorkingHours(time: string | null | undefined): boolean {
    if (!isTimeInWindow(time)) {
      return false;
    }
    const range = timeWindowRange();
    return (time as string) < range.min || (time as string) > range.max;
  }

  // --- proposing the appointment -----------------------------------------------------------------

  /** Both halves are required: `PROPOSE_VISIT` is refused server-side without a day AND a time. */
  get canSubmitAvailability(): boolean {
    return !!this.availabilityDay && isTimeInWindow(this.availabilityTime);
  }

  /**
   * The whole proposal in one submission — day, arrival time, duration and arrival plan.
   *
   * 🔴 `arrivalTime` is not optional here. `SiteVisitScheduleHandler` requires a time on
   * `PROPOSE_VISIT` (there is no second round left to agree one in), so a payload without it is
   * refused outright rather than landing a day-only proposal.
   */
  submitAvailability(): void {
    if (!this.canSubmitAvailability) {
      return;
    }
    this.dispatch('PROPOSE_VISIT', {
      day: this.availabilityDay,
      arrivalTime: this.availabilityTime,
      estimatedDuration: this.availabilityDuration?.trim() || undefined,
      arrivalPlan: this.availabilityArrivalPlan?.trim() || undefined,
    });
  }

  // --- answering the site contact's counter -------------------------------------------------------

  /** One tap: the appointment on the table is agreed as it stands, so nothing is sent with it. */
  acceptProposal(): void {
    this.dispatch('PA_ACCEPT', {});
  }

  /**
   * Open or close the counter form, seeding it from the proposal on the table the first time it is
   * filled. The mock's instruction is "change either field, or both", and that is only true if both
   * arrive already holding what the site contact proposed.
   *
   * 🔴 **Collapsing does not clear what the PA typed**, and the seed does not overwrite it — the same
   * rule {@link toggleReschedule} follows, for the same reason: the toggle is a view control sitting
   * beside a one-click Accept, so a misclick must not destroy a half-filled counter.
   */
  toggleCounter(): void {
    this.counterOpen = !this.counterOpen;
    if (!this.counterOpen) {
      return;
    }
    if (!this.counterDay) {
      this.counterDay = toDateInputValue(this.visit?.proposedDate);
    }
    if (!isTimeInWindow(this.counterTime)) {
      this.counterTime = isTimeInWindow(this.visit?.proposedTime)
        ? (this.visit?.proposedTime as string)
        : this.defaultArrivalTime;
    }
  }

  /** A counter is a whole appointment, so it carries the same required pair a proposal does. */
  get canSubmitCounter(): boolean {
    return !!this.counterDay && isTimeInWindow(this.counterTime);
  }

  /**
   * Counter with a different day, a different time, or both.
   *
   * Deliberately only those two fields: `SiteVisitWriteWhitelist`'s `PA_COUNTER` entry cannot write
   * `estimatedDuration` or `arrivalPlan`, so sending them would be a silent no-op and collecting them
   * would be a form that lies about what it changes. The duration and the plan stay PA-owned and are
   * set on the proposal.
   */
  submitCounter(): void {
    if (!this.canSubmitCounter) {
      return;
    }
    this.dispatch('PA_COUNTER', {
      day: this.counterDay,
      arrivalTime: this.counterTime,
    });
  }

  // --- moving an agreed appointment ---------------------------------------------------------------

  /**
   * Show or hide the reschedule form — the `toggleNode`/`isExpanded` pair the audit timeline's
   * related-records disclosure uses, with a single flag instead of a key set because there is exactly
   * one section here.
   *
   * 🔴 **Collapsing does NOT clear what the PA typed.** The toggle is a view control, and the trigger
   * sits one row above a Mark Complete button, so a misclick is cheap to make and would otherwise
   * silently destroy a reason and an arrival plan the PA cannot get back. The fields are cleared where
   * clearing means something — `applySchedule`, once a move has actually been accepted — and nowhere
   * else, which is also how every other form on this page behaves.
   */
  toggleReschedule(): void {
    this.rescheduleOpen = !this.rescheduleOpen;
    if (this.rescheduleOpen) {
      this.seedRescheduleTime();
    }
  }

  /**
   * Fill the reschedule's arrival time from the one already agreed, so the PA is not asked to re-enter
   * a time they settled once just to move the day. Never over a value already held — collapsing and
   * reopening must not overwrite what was typed — and never on the way closed.
   *
   * 🔴 **Shared by both ways into the form**, the disclosure toggle and the opener's intent
   * ({@link ngOnInit}). Inlining it in the toggle left the row's *Reschedule* button opening an
   * unseeded form, since that path never calls the toggle at all.
   */
  private seedRescheduleTime(): void {
    if (isTimeInWindow(this.rescheduleTime)) {
      return;
    }
    this.rescheduleTime = isTimeInWindow(this.visit?.confirmedTime)
      ? (this.visit?.confirmedTime as string)
      : this.defaultArrivalTime;
  }

  /**
   * The submit gate for the reschedule, in the shape every other form on this page uses: a getter the
   * template binds `[disabled]` to, re-asked as the first line of the submit itself.
   *
   * 🔴 **The reason is required and is trimmed before it is judged.** A whitespace-only reason is an
   * empty one — `populateReschedule` refuses it server-side, so letting it through here would trade a
   * disabled button for a confusing failure toast. Unlike `canSubmitSelection`'s counter-note, which
   * imposes a five-character minimum, this asks only for something non-blank: the requirement the PA was
   * given is "say why", and a longer floor would refuse a legitimately short answer.
   *
   * 🔴 **And bounded above at {@link maxReasonLength}, measured on the same trimmed value.** The server
   * caps the reason at its shared note length; measuring the trimmed string on both sides is what makes
   * the two agree exactly, rather than nearly — trailing whitespace must not decide the verdict here and
   * not there. The textarea's `maxlength` stops it being typed; this stops a value that reached the field
   * some other way from being posted to an API that would only refuse it.
   */
  get canSubmitReschedule(): boolean {
    const reason = this.rescheduleReason.trim();
    return (
      !!this.rescheduleDay &&
      isTimeInWindow(this.rescheduleTime) &&
      reason.length > 0 &&
      reason.length <= this.maxReasonLength
    );
  }

  /**
   * True only for the over-length case, so the form can say which rule was broken. Without it the single
   * "pick a day and say why" warning would fire at a PA who has done both — the same
   * frontend-disagrees-with-backend confusion the cap exists to prevent.
   */
  get rescheduleReasonTooLong(): boolean {
    return this.rescheduleReason.trim().length > this.maxReasonLength;
  }

  // --- post-visit completion ----------------------------------------------------------------------

  /**
   * Which outcome is selected. Clears a stale server refusal — the PA is trying something new.
   *
   * Picking `FAILED` reveals a reschedule, so its arrival time is seeded here the way the other two
   * reschedule forms seed theirs on the way open: from the time the failed visit was agreed for, which
   * is the likeliest replacement, and never over a value the PA has already chosen.
   */
  selectCompletionOutcome(outcome: 'COMPLETED' | 'FAILED'): void {
    this.completionOutcome = outcome;
    this.completionRefusal = null;
    if (outcome === 'FAILED' && !isTimeInWindow(this.completionTime)) {
      this.completionTime = isTimeInWindow(this.visit?.confirmedTime)
        ? (this.visit?.confirmedTime as string)
        : this.defaultArrivalTime;
    }
  }


  /**
   * Unchecking a group clears its files AND its cached upload ids. The handler writes an absent list
   * as an EMPTY one (see `populateCompletionReport`'s "all six fields are written every time"), so
   * stale ids here would attach a photo to a flag the PA has just turned off.
   */
  toggleCompletionAccessIssues(): void {
    this.completionAccessIssues = !this.completionAccessIssues;
    if (!this.completionAccessIssues) {
      this.completionAccessIssueFiles = [];
      this.completionAccessIssuePhotoIds = null;
    }
    this.completionRefusal = null;
  }

  toggleCompletionConcerns(): void {
    this.completionConcerns = !this.completionConcerns;
    if (!this.completionConcerns) {
      this.completionConcernFiles = [];
      this.completionConcernPhotoIds = null;
    }
    this.completionRefusal = null;
  }

  onCompletionDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  onAccessIssueFilesSelected(event: Event): void {
    this.setCompletionFiles('access', filesFromInputEvent(event));
  }

  onAccessIssueFilesDropped(event: DragEvent): void {
    event.preventDefault();
    this.setCompletionFiles('access', filesFromDropEvent(event));
  }

  onConcernFilesSelected(event: Event): void {
    this.setCompletionFiles('concern', filesFromInputEvent(event));
  }

  onConcernFilesDropped(event: DragEvent): void {
    event.preventDefault();
    this.setCompletionFiles('concern', filesFromDropEvent(event));
  }

  /** A NEW selection invalidates whatever was cached under the OLD one — see the field's own comment. */
  private setCompletionFiles(group: 'access' | 'concern', files: File[]): void {
    if (files.length === 0) {
      return;
    }
    if (group === 'access') {
      this.completionAccessIssueFiles = files;
      this.completionAccessIssuePhotoIds = null;
    } else {
      this.completionConcernFiles = files;
      this.completionConcernPhotoIds = null;
    }
    this.completionRefusal = null;
  }

  /**
   * 🔴 The live version of mock A2's error — shown the instant the box is checked with nothing
   * attached, not only after a submit attempt, and it is ALSO the wording used for the inline banner
   * when the server itself refuses for this reason (see {@link submitCompletion}). Concerns have no
   * counterpart: they are deliberately not photo-gated.
   */
  get accessIssuePhotoMissing(): boolean {
    return this.completionAccessIssues && this.completionAccessIssueFiles.length === 0;
  }

  get completionNotesTooLong(): boolean {
    return this.completionNotes.trim().length > this.maxReasonLength;
  }

  get completionReasonTooLong(): boolean {
    return this.completionReason.trim().length > this.maxReasonLength;
  }

  /**
   * The submit gate, in the same shape `canSubmitReschedule` uses. `FAILED` additionally requires the
   * replacement day, its arrival time and a non-blank, bounded reason — exactly
   * `canSubmitReschedule`'s rule — because that branch applies the very same `RESCHEDULE`, and the
   * handler asks the same three questions of it however it was reached.
   */
  get canSubmitCompletion(): boolean {
    if (this.accessIssuePhotoMissing || this.completionNotesTooLong) {
      return false;
    }
    if (this.completionOutcome !== 'FAILED') {
      return true;
    }
    const reason = this.completionReason.trim();
    return (
      !!this.completionDay &&
      isTimeInWindow(this.completionTime) &&
      reason.length > 0 &&
      reason.length <= this.maxReasonLength
    );
  }

  /**
   * Whether the browser can paint this file as an `<img>`. Chrome and Firefox cannot decode
   * `image/heic`/`image/heif` — an iPhone photo lands here with exactly that MIME (or, on some
   * browsers, none at all for the format) — and `DocumentFormatEnum` has no HEIC entry server-side
   * either, so a document created from one stores with a null format. Rather than let either produce
   * a broken `<img>`, both this local preview AND the previously-stored report (which carries no
   * format at all — see the report block's own comment) show a placeholder icon instead.
   */
  isPreviewableImage(file: File): boolean {
    const type = (file.type || '').toLowerCase();
    if (type === 'image/heic' || type === 'image/heif') {
      return false;
    }
    if (!type && /\.hei[cf]$/i.test(file.name || '')) {
      return false;
    }
    return type.startsWith('image/');
  }

  /** Memoized so the template's repeated binding does not mint a fresh object URL on every check. */
  previewUrl(file: File): string {
    let url = this.completionPreviewUrls.get(file);
    if (!url) {
      url = URL.createObjectURL(file);
      this.completionPreviewUrls.set(file, url);
    }
    return url;
  }

  /**
   * The two-step submit (Mocks A1-A3): upload each checked group's photos, THEN the transition
   * carrying the ids the uploads returned. `COMPLETE` when the visit came off, `RESCHEDULE` — the SAME
   * report — when it did not; {@link completionOutcome} selects which.
   *
   * Async rather than through the shared {@link dispatch}, which posts one call with no upload leg.
   * `async`/`await` reads the two-upload-then-transition sequence in the order it actually happens,
   * which matters here specifically because of the retry rule below.
   *
   * 🔴 **A retry must not re-upload.** If the transition is refused (or the request fails) AFTER an
   * upload already landed, a second click on Submit reuses the ids that upload returned rather than
   * posting the same files again — see the private id caches on the two fields above. Without this, a
   * PA fixing a refused note and clicking Submit again would mint a second orphaned `Document` per
   * photo on every retry.
   */
  async submitCompletion(): Promise<void> {
    if (!this.visit || this.isSubmitting || !this.canSubmitCompletion) {
      return;
    }
    const transition: SiteVisitTransition =
      this.completionOutcome === 'FAILED' ? 'RESCHEDULE' : 'COMPLETE';
    if (!this.can(transition)) {
      return;
    }
    const visitId = this.visit.id;
    this.isSubmitting = true;
    this.completionRefusal = null;
    try {
      const accessIssuePhotoIds = await this.uploadReportGroup(
        visitId,
        this.completionAccessIssues,
        this.completionAccessIssueFiles,
        'SITE_VISIT_ACCESS_ISSUE',
        this.completionAccessIssuePhotoIds,
        (ids) => (this.completionAccessIssuePhotoIds = ids)
      );
      const concernPhotoIds = await this.uploadReportGroup(
        visitId,
        this.completionConcerns,
        this.completionConcernFiles,
        'SITE_VISIT_CONCERN',
        this.completionConcernPhotoIds,
        (ids) => (this.completionConcernPhotoIds = ids)
      );
      const payload: Omit<SiteVisitTransitionPayload, 'transition'> = {
        accessIssues: this.completionAccessIssues,
        concerns: this.completionConcerns,
        completionNotes: this.completionNotes.trim(),
        accessIssuePhotoIds,
        concernPhotoIds,
        // The failed branch IS a reschedule, so it carries the same day-and-time pair the disclosure
        // above sends. Omitting the time here would refuse every failed-visit report.
        ...(transition === 'RESCHEDULE'
          ? {
              day: this.completionDay,
              arrivalTime: this.completionTime,
              reason: this.completionReason.trim(),
            }
          : {}),
      };
      const response: any = await firstValueFrom(
        this.siteVisitService.applyTransition(visitId, { transition, ...payload })
      );
      this.isSubmitting = false;
      if (response?.status === 'FAILURE') {
        this.toastr.error(response?.message || 'An error occurred. Please try again.');
        return;
      }
      const result = response?.data;
      if (!result || result.outcome !== 'APPLIED') {
        if (result?.outcome === 'INVALID_PAYLOAD') {
          // Refused, not superseded — `result.schedule` is the UNTOUCHED snapshot (see
          // `SiteVisitTransitionResult`'s own doc), so there is nothing new to adopt, and adopting it
          // anyway would run `applySchedule`'s reset and wipe exactly what the PA needs to fix and
          // resubmit — the checked boxes, the notes, the files this method just cached ids for.
          this.handleCompletionRefusal(accessIssuePhotoIds);
        } else {
          // A genuinely stale view (`VERSION_CONFLICT`, chiefly) — the negotiation moved under us, so
          // adopting the returned snapshot and clearing the form is correct here, the same as every
          // other form on this page.
          if (result?.schedule) {
            this.applySchedule(result.schedule);
          }
          this.toastr.error(this.refusalMessage(result?.outcome));
        }
        return;
      }
      this.applySchedule(result.schedule);
      this.toastr.success(
        transition === 'COMPLETE'
          ? 'Your site visit was marked complete.'
          : 'Your site visit was updated.'
      );
    } catch {
      this.isSubmitting = false;
      this.toastr.error('An error occurred. Please try again.');
    }
  }

  /**
   * `INVALID_PAYLOAD` is one outcome for several possible rules (a missing access-issue photo, notes
   * over the cap, a photo id of the wrong type on this visit, ...) and the server names none of them —
   * see `SiteVisitScheduleHandler.validateReportPhotos`, which returns its reason to nothing but a log
   * line. So this can only NARROW to the one rule this form itself already guards against and knows it
   * did not satisfy; anything else gets an honest, generic refusal rather than a guessed cause.
   */
  private handleCompletionRefusal(accessIssuePhotoIds: string[]): void {
    this.completionRefusal =
      this.completionAccessIssues && accessIssuePhotoIds.length === 0
        ? 'A photo documenting the access issue is required.'
        : 'This report could not be saved. Please check the photos and notes and try again.';
  }

  /** Upload one report group, or reuse a prior upload's ids — see {@link submitCompletion}. */
  private async uploadReportGroup(
    visitId: string,
    checked: boolean,
    files: File[],
    type: 'SITE_VISIT_ACCESS_ISSUE' | 'SITE_VISIT_CONCERN',
    cachedIds: string[] | null,
    cache: (ids: string[]) => void
  ): Promise<string[]> {
    if (!checked || files.length === 0) {
      return [];
    }
    if (cachedIds) {
      return cachedIds;
    }
    const response: any = await firstValueFrom(
      this.documentService.uploadSiteVisitDocuments(visitId, type, files)
    );
    if (response?.status === 'FAILURE' || !Array.isArray(response?.data)) {
      throw new Error('Site-visit document upload failed');
    }
    const ids: string[] = response.data;
    cache(ids);
    return ids;
  }

  /** Everything the completion panel owns, back to its opening state. Called only after a move lands. */
  private resetCompletionForm(): void {
    this.completionOutcome = 'COMPLETED';
    this.completionAccessIssues = false;
    this.completionConcerns = false;
    this.completionNotes = '';
    this.completionAccessIssueFiles = [];
    this.completionConcernFiles = [];
    this.completionAccessIssuePhotoIds = null;
    this.completionConcernPhotoIds = null;
    this.completionDay = '';
    this.completionTime = '';
    this.completionReason = '';
    this.completionRefusal = null;
    this.completionPreviewUrls.forEach((url) => URL.revokeObjectURL(url));
    this.completionPreviewUrls.clear();
  }

  /**
   * Move an already-agreed appointment — a replacement day and arrival time, an arrival plan, plus the
   * reason, which only this transition carries and only this transition requires.
   *
   * 🔴 The time is as required as the day. `RESCHEDULE` clears `confirmedDate`/`confirmedTime` and
   * puts a fresh proposal back to the site contact, so a payload carrying only a day would ask them to
   * approve half an appointment — and the handler refuses it for exactly that reason.
   */
  submitReschedule(): void {
    if (!this.canSubmitReschedule) {
      return;
    }
    this.dispatch('RESCHEDULE', {
      day: this.rescheduleDay,
      arrivalTime: this.rescheduleTime,
      arrivalPlan: this.rescheduleArrivalPlan?.trim() || undefined,
      reason: this.rescheduleReason.trim(),
    });
  }

  // --- cancellation --------------------------------------------------------------------------------

  /**
   * The submit gate, in the same shape {@link canSubmitReschedule} uses and measured the same way: the
   * **trimmed** reason, non-empty and within {@link maxReasonLength}. The server measures the trimmed
   * value too, so a reason this accepts is one the API accepts — the two agree exactly rather than
   * nearly, and trailing whitespace does not decide the verdict here and not there.
   *
   * A whitespace-only reason is therefore absence, which is the whole point of trimming first: the
   * backend treats it as absence under a rule wider than `trim()` (non-breaking spaces and the BOM
   * included), so the button must not offer to send one.
   */
  get canConfirmCancel(): boolean {
    const reason = this.cancelReason.trim();
    return reason.length > 0 && reason.length <= this.maxReasonLength;
  }

  /**
   * True only for the over-length case, so the confirmation can say which rule was broken rather than
   * repeating "say why" at somebody who has. The reschedule form's own warning splits the same way.
   */
  get cancelReasonTooLong(): boolean {
    return this.cancelReason.trim().length > this.maxReasonLength;
  }

  /** Opens the confirmation. Starts from an empty reason, so a previously abandoned one is never sent. */
  startCancel(): void {
    this.cancelReason = '';
    this.confirmingCancel = true;
  }

  abandonCancel(): void {
    this.cancelReason = '';
    this.confirmingCancel = false;
  }

  /**
   * 🔴 **Carries the reason.** This sent `dispatch('CANCEL', {})` — an empty payload — and the server
   * now refuses that, so the guard is not cosmetic: without it a blank reason reaches an API that can
   * only answer `INVALID_PAYLOAD`. Trimmed, which is the value the server stores.
   */
  cancelVisit(): void {
    if (!this.canConfirmCancel) {
      return;
    }
    this.dispatch('CANCEL', { reason: this.cancelReason.trim() });
  }

  // --- resend link and calendar ------------------------------------------------------------------

  resendSiteContactLink(): void {
    if (!this.canResendLink || !this.visit || this.isSubmitting) {
      return;
    }
    this.isSubmitting = true;
    this.siteVisitService.resendSiteContactLink(this.visit.id).subscribe({
      next: (response: any) => {
        this.isSubmitting = false;
        if (response?.status === 'FAILURE') {
          this.toastr.error(
            response?.message || 'An error occurred. Please try again.'
          );
          return;
        }
        this.toastr.success(
          `A new scheduling link was sent to the ${this.awaitingParty}.`
        );
      },
      error: () => {
        this.isSubmitting = false;
        this.toastr.error('An error occurred. Please try again.');
      },
    });
  }

  openCalendarLink(): void {
    if (!this.visit || this.isSubmitting) {
      return;
    }
    this.isSubmitting = true;
    this.siteVisitService.getCalendarLink(this.visit.id).subscribe({
      next: (response: any) => {
        this.isSubmitting = false;
        const url = response?.data?.url;
        if (response?.status === 'FAILURE' || !url) {
          this.toastr.error(
            response?.message || 'An error occurred. Please try again.'
          );
          return;
        }
        // The URL is minted and token-addressed server-side; this side never composes one.
        this.calendarUrl = url;
        window.open(url, '_blank');
      },
      error: () => {
        this.isSubmitting = false;
        this.toastr.error('An error occurred. Please try again.');
      },
    });
  }

  // --- the one dispatch path ----------------------------------------------------------------------

  /**
   * Every move goes through here, and the first thing it does is re-ask the transition table. The
   * template already hides an illegal affordance; this makes it unreachable rather than merely
   * invisible, so a stale view or a programmatic caller cannot post a move the state forbids.
   */
  private dispatch(
    transition: SiteVisitTransition,
    payload: Omit<SiteVisitTransitionPayload, 'transition'>
  ): void {
    if (!this.visit || this.isSubmitting || !this.can(transition)) {
      return;
    }
    this.isSubmitting = true;
    this.siteVisitService
      .applyTransition(this.visit.id, { transition, ...payload })
      .subscribe({
        next: (response: any) => {
          this.isSubmitting = false;
          if (response?.status === 'FAILURE') {
            this.toastr.error(
              response?.message || 'An error occurred. Please try again.'
            );
            return;
          }
          const result = response?.data;
          if (!result || result.outcome !== 'APPLIED') {
            // The negotiation moved under us, or the move was refused. The result carries the
            // snapshot we acted on, so adopt it and let the state redraw rather than guessing.
            if (result?.schedule) {
              this.applySchedule(result.schedule);
            }
            this.toastr.error(this.refusalMessage(result?.outcome));
            return;
          }
          this.applySchedule(result.schedule);
          this.toastr.success('Your site visit was updated.');
        },
        error: () => {
          this.isSubmitting = false;
          this.toastr.error('An error occurred. Please try again.');
        },
      });
  }

  /**
   * `VERSION_CONFLICT` means the view was stale, which is genuinely "this has moved on". Every other
   * non-`APPLIED` outcome this shared path can still reach (`ILLEGAL_TRANSITION`,
   * `ACTOR_NOT_PERMITTED`) should already be unreachable — `can()` refuses those before the call goes
   * out — so the same copy stands in for them rather than inventing text for a path that should be
   * dead. `INVALID_PAYLOAD` is handled on its own by {@link handleCompletionRefusal} for the
   * completion form specifically; nothing else on this page sends a payload the server can refuse for
   * a reason worth narrating, so this generic branch is enough for the rest.
   */
  private refusalMessage(outcome: SiteVisitTransitionOutcome | undefined): string {
    return outcome === 'INVALID_PAYLOAD'
      ? 'This move could not be saved. Please check what you entered and try again.'
      : 'This site visit has moved on. The latest state is shown.';
  }

  /** Adopt the negotiation the server returned and reset the forms the old state owned. */
  private applySchedule(schedule: SiteVisitView): void {
    this.visit = schedule;
    this.confirmingCancel = false;
    this.cancelReason = '';
    this.counterDay = '';
    this.counterTime = '';
    this.rescheduleDay = '';
    this.rescheduleTime = '';
    this.rescheduleReason = '';
    this.rescheduleArrivalPlan = '';
    this.resetCompletionForm();
    // Both disclosures collapse along with the fields they show. This method runs after EVERY accepted
    // move, not only a reschedule or a counter, which is the behavior wanted either way: the fields
    // above have just been wiped, so leaving either section open would present an emptied form as if
    // it were mid-edit. Each re-seeds itself from the new snapshot the next time it is opened.
    this.rescheduleOpen = false;
    this.counterOpen = false;
    this.availabilityTime = this.defaultArrivalTime;
    this.visitChange.emit(schedule);
  }
}

/** The chosen files off a `<input type="file">` change event, browse-to-select's half of a dropzone. */
function filesFromInputEvent(event: Event): File[] {
  const files = (event?.target as HTMLInputElement | null)?.files;
  return files ? Array.from(files) : [];
}

/** The dropped files off a drag-and-drop event, the other half of the same dropzone. */
function filesFromDropEvent(event: DragEvent): File[] {
  const files = event?.dataTransfer?.files;
  return files ? Array.from(files) : [];
}
